/**
 * File: useSearchFeedback.ts
 * Description: Custom hook for handling search feedback logic
 * Author: System
 * Created: 2025-10-06
 * Last Modified: 2025-10-06
 */

import { useCallback } from 'react';

import { contextManager } from '../../services/contextManager';
import FeedbackService, {
  FeedbackCreateRequest,
  SearchResultFeedbackItem,
} from '../../services/feedbackService';
import { logger } from '../../utils/logger';

import { ExistingDocument } from './ContextBrowser';
import { SearchResultFeedbackData, SearchResultItem } from './SearchResultsFeedback';

export const useSearchFeedback = (filteredDocuments: ExistingDocument[]) => {
  const handleFeedback = useCallback(async (
    feedbackData: SearchResultFeedbackData[],
    reorderedResults: SearchResultItem[],
    includeContext: boolean,
    includeUserName: boolean,
    searchQuery?: string,
  ) => {
    
    try {
      // Get username from localStorage if includeUserName is true
      const username = includeUserName 
        ? JSON.parse(localStorage.getItem('auth_user') || '{}')?.username 
        : undefined;
      
      // Get current context for enhanced feedback
      const context = contextManager.getContext();
      
      // Create the new structured feedback data as a list of result objects
      const searchResultsFeedback: SearchResultFeedbackItem[] = reorderedResults.map((result, newIndex) => {
        // Find the original index of this result
        const originalIndex = filteredDocuments.findIndex(doc => doc.id === result.id);
        
        // Find any feedback for this result
        const resultFeedback = feedbackData.find(feedback => feedback.resultId === result.id);
        
        // Extract chunk metadata from the first chunk if available
        const firstChunk = result.chunks?.[0];
        const chunkMetadata = firstChunk ? {
          chunk_id: firstChunk.chunk_id,
          document_id: firstChunk.document_id,
          chunk_type: firstChunk.chunk_type,
          chunk_order: firstChunk.chunk_order,
          keywords: firstChunk.keywords,
          sector: firstChunk.sector,
          url: firstChunk.url,
          change_date: firstChunk.change_date,
        } : undefined;
        
        return {
          resultId: result.id,
          originalRank: originalIndex + 1, // 1-based ranking
          newRank: newIndex + 1, // 1-based ranking
          feedbackData: {
            // Core feedback fields
            feedback_type: resultFeedback?.feedback === 'up' ? 'thumbs_up' : 
              resultFeedback?.feedback === 'down' ? 'thumbs_down' : null,
            comment: resultFeedback?.comment || '',
            
            // Result metadata
            resultName: result.name,
            new_rank: newIndex + 1, // Alias for newRank
            relevanceScore: result.relevanceScore,
            source: result.source,
            description: result.description,
            summary: result.summary,
            
            // Additional metadata from search items
            category: result.category,
            size: result.size,
            lastModified: result.lastModified?.toISOString(),
            author: result.author,
            tags: result.tags,
            path: result.path,
            // Chunk metadata
            chunkMetadata,
            // Search metadata from backend
            searchMetadata: result.searchMetadata,
          },
        };
      });

      // Submit as a single comprehensive feedback entry
      const apiRequestData: FeedbackCreateRequest = {
        project_id: 1, // Default project ID
        section: 'search',
        agent: 'SearchSummaryAgent',
        feedback_data: {
          // Core feedback metadata
          feedback_type: 'thumbs_up', // Default value
          feedbackCategory: 'search_results_ranking',
          includeContext,
          includeUserName,
          username,
          timestamp: new Date().toISOString(),
          
          searchResultsFeedback,
          
          // Summary metadata
          searchMetadata: {
            searchQuery: searchQuery || '',
            totalResults: reorderedResults.length,
            resultsWithFeedback: feedbackData.filter(f => f.feedback !== null).length,
          },
          
          // Execution context
          execution_context: {
            searchContext: includeContext ? (searchQuery || 'Search results feedback') : undefined,
          },
        },
      };

      await FeedbackService.submitFeedback(apiRequestData);
      
    } catch (error) {
      logger.error('Error submitting search feedback:', error);
    }
  }, [filteredDocuments]);

  return { handleFeedback };
};